import { useState, useEffect, useRef, useCallback } from "react";
import { FeeDue } from "../types";
import { getFeeDuesByStudent } from "../services/feeDuesService";
import { getLoggedInUserAdmissionNo } from "../../../modules/common/user/UserService";

const POLLING_INTERVAL_MS = 15000;

// Rolls a student's per-term fee dues up into one FeeDue.
export const aggregateFeeDues = (tmpFeeDues: FeeDue[], admissionNo: string): FeeDue | undefined => {
  if (tmpFeeDues.length === 0) return undefined;
  let totalPayable = 0;
  let totalPreviousDue = 0;

  for (const fd of tmpFeeDues) {
    totalPayable += fd.payable;
    totalPreviousDue += fd.previousTotalDue;
  }

  return {
    studentName: tmpFeeDues[0].studentName,
    admissionNo: admissionNo,
    guardianPhoneNo: tmpFeeDues[0].guardianPhoneNo,
    gradeName: tmpFeeDues[0].gradeName,
    sectionName: tmpFeeDues[0].sectionName,
    terms: "",
    payable: totalPayable,
    academicYear: tmpFeeDues[0].academicYear,
    previousTotalDue: totalPreviousDue,
    paymentOrderInProgress: tmpFeeDues[0].paymentOrderInProgress,
    paymentLink: tmpFeeDues[0].paymentLink,
  };
};

export const useStudentFeeDues = () => {
  const [feeDues, setFeeDues] = useState<FeeDue[]>([]);
  const [aggregatedFeeDue, setAggregatedFeeDue] = useState<FeeDue>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentInProgress, setPaymentInProgress] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [activePoStatus, setActivePoStatus] = useState<string | undefined>(undefined);
  const [admissionNo, setAdmissionNo] = useState<string>(getLoggedInUserAdmissionNo());

  const paymentInProgressRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const calculateAggregateFeeDue = (tmpFeeDues: FeeDue[]) => {
    const aggFD = aggregateFeeDues(tmpFeeDues, admissionNo);
    if (aggFD) setAggregatedFeeDue(aggFD);
  };
  const fetchFeeDues = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getFeeDuesByStudent(admissionNo);
      // TODO - make this composite object - PIP
      const inProgress = data.find((fd) => fd.paymentOrderInProgress === true);
      const isInProgress = !!inProgress;
      paymentInProgressRef.current = isInProgress;
      setPaymentInProgress(isInProgress);
      setPaymentLink(inProgress?.paymentLink ?? "");
      setActivePoStatus(inProgress?.activePoStatus);
      setFeeDues(data);
      calculateAggregateFeeDue(data);
    } catch (err) {
      setError("Failed to fetch fee dues.");
    } finally {
      setLoading(false);
    }
  }, [admissionNo]);

  useEffect(() => {
    if (paymentInProgress) {
      intervalRef.current = setInterval(() => {
        if (paymentInProgressRef.current) {
          fetchFeeDues();
        }
      }, POLLING_INTERVAL_MS);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [paymentInProgress, fetchFeeDues]);

  useEffect(() => {
    fetchFeeDues();
  }, [fetchFeeDues]);

  return { feeDues, loading, error, paymentInProgress, paymentLink, activePoStatus, fetchFeeDues, aggregatedFeeDue };
};
